import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { mediaSrc } from '../api/media';
import SharingModal from '../components/SharingModal';

type Relation = 'NONE' | 'FRIENDS' | 'REQUEST_SENT' | 'REQUEST_RECEIVED';

interface SearchResult {
  id: number;
  name: string;
  profilePhotoUrl: string | null;
  relation: Relation;
}
interface FriendItem {
  friendshipId: number;
  parentId: number;
  name: string;
  profilePhotoUrl: string | null;
}
interface RequestItem {
  id: number;
  parentId: number;
  name: string;
  profilePhotoUrl: string | null;
  createdAt: string;
}
interface FriendMediaItem {
  id: number;
  type: 'PHOTO' | 'VIDEO';
  filename: string;
  url: string;
}

const Avatar: React.FC<{ url: string | null; name: string; size?: number }> = ({ url, name, size = 40 }) => (
  url ? (
    <img src={mediaSrc(url)} alt={name} style={{ width: size, height: size }}
         className="rounded-full object-cover border border-indigo-200" />
  ) : (
    <div style={{ width: size, height: size }}
         className="rounded-full bg-indigo-100 flex items-center justify-center text-lg">👤</div>
  )
);

const ParentFriends: React.FC = () => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResult[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [friends, setFriends] = useState<FriendItem[]>([]);
  const [incoming, setIncoming] = useState<RequestItem[]>([]);
  const [viewing, setViewing] = useState<{ name: string; url: string | null } | null>(null);
  const [sharingWith, setSharingWith] = useState<{ id: number; name: string } | null>(null);
  const [friendMedia, setFriendMedia] = useState<{ name: string; items: FriendMediaItem[]; loading: boolean } | null>(null);
  const navigate = useNavigate();

  const openChat = async (parentId: number) => {
    try {
      const r = await api.post(`/api/parent/chats/direct/${parentId}`);
      navigate(`/parent/social?tab=chat&cid=${r.data.conversationId}`);
    } catch { alert('Could not open chat'); }
  };

  // Browse a friend's media that they've shared with friends (photos + videos).
  const viewFriendMedia = async (parentId: number, name: string) => {
    setFriendMedia({ name, items: [], loading: true });
    try {
      const [photos, videos] = await Promise.all([
        api.get(`/api/parent/friends/${parentId}/media`, { params: { type: 'PHOTO' } }),
        api.get(`/api/parent/friends/${parentId}/media`, { params: { type: 'VIDEO' } }),
      ]);
      setFriendMedia({ name, items: [...photos.data, ...videos.data], loading: false });
    } catch (err: any) {
      setFriendMedia({ name, items: [], loading: false });
      alert(err.response?.data?.message || 'Could not load shared media');
    }
  };

  const refresh = async () => {
    try {
      const [fr, inc] = await Promise.all([
        api.get('/api/parent/friends'),
        api.get('/api/parent/friends/requests/incoming'),
      ]);
      setFriends(fr.data);
      setIncoming(inc.data);
    } catch {}
  };

  useEffect(() => { refresh(); }, []);

  const doSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    setSearching(true);
    try {
      const res = await api.get('/api/parent/search', { params: { q } });
      setResults(res.data);
    } catch {} finally { setSearching(false); }
  };

  // re-run search (to refresh relation labels) without changing the box
  const reSearch = async () => {
    if (!q.trim()) { setResults(null); return; }
    try {
      const res = await api.get('/api/parent/search', { params: { q } });
      setResults(res.data);
    } catch {}
  };

  const sendRequest = async (parentId: number) => {
    try { await api.post('/api/parent/friends/requests', { parentId }); await Promise.all([reSearch(), refresh()]); }
    catch (err: any) { alert(err.response?.data?.message || 'Failed to send request'); }
  };
  const accept = async (id: number) => { await api.post(`/api/parent/friends/requests/${id}/accept`); await Promise.all([refresh(), reSearch()]); };
  const decline = async (id: number) => { await api.delete(`/api/parent/friends/requests/${id}`); await Promise.all([refresh(), reSearch()]); };
  const unfriend = async (parentId: number) => {
    if (!window.confirm('Unfriend this parent?')) return;
    await api.delete(`/api/parent/friends/${parentId}`);
    await Promise.all([refresh(), reSearch()]);
  };

  const relationButton = (r: SearchResult) => {
    switch (r.relation) {
      case 'FRIENDS': return <span className="text-xs text-green-600 font-medium">✓ Friends</span>;
      case 'REQUEST_SENT': return <span className="text-xs text-gray-400">Requested</span>;
      case 'REQUEST_RECEIVED': return (
        <button onClick={() => acceptByParent(r.id)} className="text-xs bg-green-500 text-white px-3 py-1 rounded-lg">Accept</button>
      );
      default: return (
        <button onClick={() => sendRequest(r.id)} className="text-xs bg-parentaccent text-white px-3 py-1 rounded-lg">Add friend</button>
      );
    }
  };

  // accept when we only know the other parentId (find the incoming request id)
  const acceptByParent = async (parentId: number) => {
    const req = incoming.find((i) => i.parentId === parentId);
    if (req) await accept(req.id);
  };

  return (
    <div>
      <main className="space-y-6">
        <h1 className="text-xl font-display font-extrabold text-gray-800">🤝 Friends</h1>

        {/* Search */}
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-3">Find a parent</h2>
          <form onSubmit={doSearch} className="flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email"
              className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
            <button type="submit" disabled={searching}
              className="bg-parentaccent text-white text-sm px-4 py-2 rounded-lg disabled:opacity-50">
              {searching ? '…' : 'Search'}
            </button>
          </form>
          {results && (
            <div className="mt-4 space-y-2">
              {results.length === 0 && <p className="text-sm text-gray-400">No parents found.</p>}
              {results.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3">
                  <button className="flex items-center gap-3 text-left"
                    onClick={() => setViewing({ name: r.name, url: r.profilePhotoUrl })}>
                    <Avatar url={r.profilePhotoUrl} name={r.name} />
                    <span className="text-sm font-medium text-gray-700">{r.name}</span>
                  </button>
                  {relationButton(r)}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Incoming requests */}
        {incoming.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm p-5">
            <h2 className="font-semibold text-gray-700 mb-3">Friend requests</h2>
            <div className="space-y-2">
              {incoming.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Avatar url={r.profilePhotoUrl} name={r.name} />
                    <span className="text-sm font-medium text-gray-700">{r.name}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => accept(r.id)} className="text-xs bg-green-500 text-white px-3 py-1 rounded-lg">Accept</button>
                    <button onClick={() => decline(r.id)} className="text-xs text-gray-500 border border-gray-200 px-3 py-1 rounded-lg">Decline</button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* My friends */}
        <section className="bg-white rounded-2xl shadow-sm p-5">
          <h2 className="font-semibold text-gray-700 mb-3">My friends ({friends.length})</h2>
          {friends.length === 0 ? (
            <p className="text-sm text-gray-400">No friends yet. Search above to add one.</p>
          ) : (
            <div className="space-y-2">
              {friends.map((f) => (
                <div key={f.friendshipId} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <button className="flex items-center gap-3 text-left min-w-0"
                    onClick={() => setViewing({ name: f.name, url: f.profilePhotoUrl })}>
                    <Avatar url={f.profilePhotoUrl} name={f.name} />
                    <span className="text-sm font-medium text-gray-700 truncate">{f.name}</span>
                  </button>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => viewFriendMedia(f.parentId, f.name)}
                      className="text-xs text-parentaccent border border-indigo-200 hover:bg-indigo-50 px-3 py-1 rounded-lg">📷 Photos</button>
                    <button onClick={() => openChat(f.parentId)}
                      className="text-xs text-parentaccent border border-indigo-200 hover:bg-indigo-50 px-3 py-1 rounded-lg">💬 Chat</button>
                    <button onClick={() => setSharingWith({ id: f.parentId, name: f.name })}
                      className="text-xs text-parentaccent border border-indigo-200 hover:bg-indigo-50 px-3 py-1 rounded-lg">Manage sharing</button>
                    <button onClick={() => unfriend(f.parentId)}
                      className="text-xs text-red-400 hover:text-red-600 border border-red-200 px-3 py-1 rounded-lg">Unfriend</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {sharingWith && (
        <SharingModal friendId={sharingWith.id} friendName={sharingWith.name} onClose={() => setSharingWith(null)} />
      )}

      {/* A friend's media shared with friends */}
      {friendMedia && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setFriendMedia(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-700">{friendMedia.name}'s shared media</h3>
              <button onClick={() => setFriendMedia(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
            </div>
            {friendMedia.loading ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : friendMedia.items.length === 0 ? (
              <p className="text-sm text-gray-400">Nothing shared with you yet.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {friendMedia.items.map((m) => (
                  <div key={m.id} className="bg-gray-50 rounded-xl overflow-hidden">
                    {m.type === 'PHOTO' ? (
                      <img src={mediaSrc(m.url)} alt={m.filename} className="w-full aspect-square object-cover" />
                    ) : (
                      <video src={mediaSrc(m.url)} controls className="w-full aspect-square object-cover bg-black" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo-only profile view */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setViewing(null)}>
          <div className="bg-white rounded-2xl p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <Avatar url={viewing.url} name={viewing.name} size={140} />
            <p className="mt-3 font-semibold text-gray-700">{viewing.name}</p>
            <p className="text-xs text-gray-400 mt-1">Profile is private — photo and name only</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentFriends;
